import { api } from "@clarent/backend/convex/_generated/api";
import type { Doc } from "@clarent/backend/convex/_generated/dataModel";
import { shopWeeklyHours, type EngineSettings } from "@clarent/scheduling";
import { useQuery } from "convex/react";
import { useMemo } from "react";

export interface ShopMachines {
  /** Every machine, archived ones included, in the order they were added. */
  all: Doc<"machines">[];
  /** Machines that can be scheduled. */
  active: Doc<"machines">[];
  /** Total weekly hours across the active machines. */
  weeklyHours: number;
  /** What to call a machine id on a step: its name, or why it has none. */
  name: (machineId: string) => string;
}

/** The organisation's machines (D49), or undefined while loading. */
export function useMachines(): ShopMachines | undefined {
  const all = useQuery(api.machines.list);
  return useMemo(() => {
    if (!all) return undefined;
    const active = all.filter((m) => m.archivedAt === undefined);
    const byId = new Map(all.map((m) => [m._id as string, m]));
    return {
      all,
      active,
      weeklyHours: shopWeeklyHours(active.map((m) => ({ id: m._id, weeklyHours: m.weeklyHours }))),
      name: (machineId) => {
        const machine = byId.get(machineId);
        if (!machine) return "No machine";
        return machine.archivedAt === undefined ? machine.name : `${machine.name} (archived)`;
      },
    };
  }, [all]);
}

/** The engine's settings for the shop, as the backend builds them: active machines only. */
export function engineSettings(settings: Pick<Doc<"settings">, "workingDays" | "alertLeadDays">, machines: ShopMachines): EngineSettings {
  return {
    workingDays: settings.workingDays,
    machines: machines.active.map((m) => ({ id: m._id, weeklyHours: m.weeklyHours })),
    alertLeadDays: settings.alertLeadDays,
  };
}
